import React, { useState, useMemo, useEffect } from 'react';
import {
  WarningIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  AlertContainer as Alert,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import {
  SingleSelect,
  type SingleSelectOption,
} from 'src/components/common/SingleSelect';
import { useGetAgreementImportContext } from 'src/api/rpc/hooks/rebates/useHistoricalImport';
import useRebateAgreements from 'src/api/rebates/useRebateAgreements';
import useRebateAgreementYears from 'src/api/rebates/useRebateAgreementYears';
import type { GetAgreementImportContextResponse } from '@erp/rpc';

interface Step1SelectAgreementProps {
  onComplete: (
    context: GetAgreementImportContextResponse,
    year: number,
  ) => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
}

function Step1SelectAgreement({
  onComplete,
  onError,
  isLoading,
  error,
}: Step1SelectAgreementProps) {
  const [selectedAgreement, setSelectedAgreement] =
    useState<SingleSelectOption | null>(null);
  const [selectedYear, setSelectedYear] = useState<SingleSelectOption | null>(
    null,
  );

  // Fetch available years
  const { data: yearsData, isLoading: yearsLoading } =
    useRebateAgreementYears();
  const availableYears = yearsData?.years || [];

  // Convert years to SingleSelect options (sorted descending)
  const yearOptions: SingleSelectOption[] = availableYears
    .sort((a, b) => b - a) // Newest first
    .map((year) => ({
      id: year.toString(),
      label: year.toString(),
      value: year,
    }));

  // Default to 2025 when years are loaded
  useEffect(() => {
    if (yearOptions.length > 0 && !selectedYear) {
      const year2025 = yearOptions.find((opt) => opt.value === 2025);
      if (year2025) {
        setSelectedYear(year2025);
      } else if (yearOptions.length > 0) {
        // If 2025 not available, select the first (newest) year
        setSelectedYear(yearOptions[0]);
      }
    }
  }, [yearOptions, selectedYear]);

  // Get selected year value for filtering
  const selectedYearValue = selectedYear?.value as number | undefined;

  // Fetch agreements filtered by selected year
  const queryParams = useMemo(
    () => (selectedYearValue ? { years: [selectedYearValue] } : undefined),
    [selectedYearValue],
  );
  const { data: agreements, isLoading: agreementsLoading } =
    useRebateAgreements(queryParams, !!selectedYearValue);

  // Convert agreements to SingleSelect options
  const agreementOptions: SingleSelectOption[] = useMemo(() => {
    if (!agreements) return [];
    return agreements.map((agreement) => ({
      id: agreement.id,
      label: `${agreement.name} (${agreement.supplier.name})`,
      value: agreement,
    }));
  }, [agreements]);

  // Auto-fetch context when agreement is selected
  const agreementId = selectedAgreement?.id || '';
  const {
    data: context,
    isLoading: isFetchingContext,
    error: fetchError,
  } = useGetAgreementImportContext(
    { agreementId },
    !!agreementId && !!selectedAgreement,
  );

  const handleAgreementChange = (option: SingleSelectOption | null) => {
    setSelectedAgreement(option);
  };

  const handleYearChange = (option: SingleSelectOption | null) => {
    setSelectedYear(option);
    // Clear selected agreement when year changes since agreements list will change
    setSelectedAgreement(null);
  };

  const handleContinue = () => {
    if (!context) {
      onError('Please select an agreement');
      return;
    }

    if (!selectedYear) {
      onError('Please select a year');
      return;
    }

    if (!context.rexelCode) {
      onError(
        'No rexel_code found in company metadata. Please ensure the company has a rexel_code set.',
      );
      return;
    }

    const year = selectedYear.value as number;
    onComplete(context, year);
  };

  const canContinue =
    !!context && !!selectedYear && !!context.rexelCode && !isFetchingContext;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">Select Agreement</h2>
        <p className="text-muted-foreground">
          Select an agreement and year to import historical data for. The system
          will look up the supplier and company to find the rexel code for
          filtering CSV transactions.
        </p>
      </div>

      {/* Year Selection */}
      <div className="space-y-2">
        <SingleSelect
          label="Transaction Year"
          options={yearOptions}
          value={selectedYear}
          onChange={handleYearChange}
          placeholder="Select year..."
          searchPlaceholder="Search years..."
          disabled={yearsLoading}
          fullWidth
          shouldAutoSort={false}
        />
        <p className="text-xs text-muted-foreground">
          Only transactions from this year will be imported
        </p>
      </div>

      {/* Agreement Selection */}
      <div className="space-y-2">
        <SingleSelect
          label="Agreement"
          options={agreementOptions}
          value={selectedAgreement}
          onChange={handleAgreementChange}
          placeholder="Select agreement..."
          searchPlaceholder="Search agreements..."
          disabled={agreementsLoading}
          fullWidth
        />
      </div>

      {/* Error Display */}
      {(error || fetchError) && (
        <Alert variant="destructive">
          <WarningIcon className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error ||
              (fetchError instanceof Error
                ? fetchError.message
                : 'Failed to fetch agreement')}
          </AlertDescription>
        </Alert>
      )}

      {/* Loading Context */}
      {selectedAgreement && isFetchingContext && (
        <div className="rounded-lg border p-4">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-sm text-muted-foreground">
              Loading agreement details...
            </span>
          </div>
        </div>
      )}

      {/* Context Display */}
      {context && !isFetchingContext && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
            <span className="font-medium">Agreement Found</span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Agreement:</span>
              <p className="font-medium">{context.agreementName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Supplier:</span>
              <p className="font-medium">{context.supplierName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Company:</span>
              <p className="font-medium">{context.companyName || 'N/A'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Rexel Code:</span>
              <p
                className={`font-medium ${context.rexelCode ? 'text-green-600' : 'text-red-500'}`}
              >
                {context.rexelCode || 'NOT FOUND'}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Date Range:</span>
              <p className="font-medium">
                {context.startDate} - {context.endDate}
              </p>
            </div>
          </div>

          {!context.rexelCode && (
            <Alert variant="destructive" className="mt-2">
              <WarningIcon className="h-4 w-4" />
              <AlertDescription>
                No rexel_code found in company metadata. Please set the
                rexel_code in the company settings before importing.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Continue Button */}
      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={!canContinue || isLoading}>
          Continue
          <ArrowRightIcon className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

export default Step1SelectAgreement;
